import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './BulkGiftingModal.scss';
import { connect } from 'react-redux';
import cn from 'classnames';
import moment from 'moment';
import _ from 'lodash';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/pro-light-svg-icons';
import { faArrowLeft, faLocationArrow, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { confirmAlert } from 'react-confirm-alert';

import { openArtistModal, closeGiftingModal } from '../../Actions/UIActions';
import { sendLookbookOffPlatformInvitation, getBrandLookbookInvites } from '../../APIClient/lookbook_invites';
import { createSamplesRequest } from '../../Actions/AnalyticsActions';
import { updateBrandSettings } from '../../Actions/BrandActions';
import { getBrandRemaining } from '../../APIClient/brands';
import {
  isAdminControlMode,
  getBulkGiftingModalStartingParams,
  getBulkGiftingModalCloseCallback,
  getBulkGiftingModalSubmitCallback,
  isArtistModalVisible
} from '../../Helpers/ui_helpers';
import { getBrand, getBrandLists, getBrandLookbooks } from '../../Helpers/user_helpers';
import { isSubscribedToFeature } from '../../Helpers/subscription_helpers';

import OutsideClickHandler from 'react-outside-click-handler';
import Modal from '../General/Modal';
import DateSelector from '../General/DateSelector';
import BulkGiftingModalSearch from './BulkGiftingModalSearch';
import BulkGiftingModalSelectedUsers from './BulkGiftingModalSelectedUsers';
import BulkGiftingModalMessage from './BulkGiftingModalMessage';

import Loader from '../Loader/Loader';
import Tooltip from '../General/Tooltip';

const BulkGiftingModal = props => {
  const { user, talent: brandTalent, createSamplesRequest, openArtistModal, ui, analytics } = props;
  const talent = brandTalent.talent;
  const brand = getBrand(user);
  const lists = getBrandLists(user);
  const lookbooks = getBrandLookbooks(user);
  const requests = analytics?.brandAnalytics?.requests;
  const startingParams = getBulkGiftingModalStartingParams(ui);
  const closeCallback = getBulkGiftingModalCloseCallback(ui);
  const submitCallback = getBulkGiftingModalSubmitCallback(ui);
  const warningOnClose = startingParams.warningOnClose;
  const mostRecentlySentRequest = _.orderBy(requests, 'createdAt', 'desc')[0];

  const [offPlatformLookbookInvites, setOffPlatformLookbookInvites] = useState([]);
  const [brandGiftingRemaining, setBrandGiftingRemaining] = useState(null);
  const [brandGiftingReupped, setBrandGiftingReupped] = useState(null);
  const [selectedGiftingOption, setSelectedGiftingOption] = useState(
    startingParams.Lookbook_id
      ? lookbooks.find(l => l.id === parseInt(startingParams.Lookbook_id || mostRecentlySentRequest?.Lookbook_id)) || {
          title: 'General Gifting Request'
        }
      : { title: 'General Gifting Request' }
  );
  const [giftingOptionsSelectIsOpen, setGiftingOptionsSelectIsOpen] = useState(false);
  const openGiftingOptionsSelect = () => setGiftingOptionsSelectIsOpen(true);
  const closeGiftingOptionsSelect = () => setGiftingOptionsSelectIsOpen(false);
  const selectedLookbookId = selectedGiftingOption?.id;
  const close = () => {
    const completeClose = () => {
      props.closeGiftingModal();
      closeCallback && closeCallback();
    };
    if (warningOnClose) {
      setForceOuter(false);
      confirmAlert({
        title: 'Just Confirming?',
        message: warningOnClose,
        buttons: [
          {
            label: 'Cancel',
            className: 'cancel',
            onClick: () => setForceOuter(true)
          },
          {
            label: 'Close Modal',
            onClick: completeClose
          }
        ]
      });
    } else {
      completeClose();
    }
  };
  const closeAfterSubmission = () => {
    props.closeGiftingModal();
    submitCallback && submitCallback();
  };

  // no loading or error state because we want this strictly in the background
  useEffect(() => {
    getBrandLookbookInvites(brand.id)
      .then(resp => setOffPlatformLookbookInvites(resp.lookbookInvites))
      .catch(err => console.error({ err }));

    getBrandRemaining(user)
      .then(resp => {
        setBrandGiftingRemaining(Math.max(resp.gifting_remaining, 0));
        setBrandGiftingReupped(resp.gifting_requests_reupped);
      })
      .catch(err => console.error({ err }));
  }, []);

  const [isFocused, setIsFocused] = useState(false);
  const [curSearchVal, setCurSearchVal] = useState('');
  const handleFocused = () => setIsFocused(true);
  const handleBlurred = e => {
    e.stopPropagation();
    setIsFocused(false);
    setCurSearchVal('');
  };

  const [platformUsersToAdd, setPlatformUsersToAdd] = useState(startingParams.preselectedUsers || []);
  const [emailUsersToAdd, setEmailUsersToAdd] = useState([]);
  const [message, setMessage] = useState('');
  const [expiresOn, setExpiresOn] = useState(null);
  const [invalidLocationUserIds, setInvalidLocationUserIds] = useState([]);

  const selectedUserIdSet = new Set(platformUsersToAdd.map(u => u.id));
  const selectedEmailSet = new Set(emailUsersToAdd.map(u => u.email));
  const [selectedUsersApiState, setSelectedUsersApiState] = useState({});
  const selectedUsersApiStateRef = useRef({});

  const [isSendingRequests, setIsSendingRequests] = useState(false);
  const usersHaveBeenAdded = !!platformUsersToAdd.length || !!emailUsersToAdd.length;
  const someRequestsErrored = Object.values(selectedUsersApiState).some(s => s.error);
  const allRequestsSuccessful = Object.values(selectedUsersApiState).every(s => s.success);

  useEffect(() => {
    if (selectedGiftingOption?.allowedCountryCodes) {
      const allowedCountryCodesArr = selectedGiftingOption.allowedCountryCodes.split(',');
      const invalidUserIds = platformUsersToAdd.filter(u => !allowedCountryCodesArr.includes(u.countryCode)).map(u => u.id);
      setInvalidLocationUserIds(invalidUserIds);
    } else {
      setInvalidLocationUserIds([]);
    }
  }, [platformUsersToAdd, selectedGiftingOption]);

  // UI Handling so that we can show modals correctly stacked, since we can show the bulk gifting and artist modal at the same time
  const [forceOuter, setForceOuter] = useState(isArtistModalVisible(ui));

  const userIsAlreadySelected = user => {
    if (user.id && selectedUserIdSet.has(user.id)) return true;
    else if (user.email && selectedEmailSet.has(user.email)) return true;
    else return false;
  };

  const selectUsers = users => {
    const usersToAdd = users.filter(u => !userIsAlreadySelected(u));

    if (usersToAdd.length === 1) {
      const user = usersToAdd[0];

      if (!user.id) setEmailUsersToAdd([...emailUsersToAdd, user]);
      else setPlatformUsersToAdd([...platformUsersToAdd, user]);
    } else {
      setPlatformUsersToAdd([...platformUsersToAdd, ...usersToAdd]);
    }
  };

  const removeUser = user => {
    if (user.id) setPlatformUsersToAdd(platformUsersToAdd.filter(u => u.id !== user.id));
    else setEmailUsersToAdd(emailUsersToAdd.filter(u => u.email !== user.email));
  };

  const sendRequests = async () => {
    if (!usersHaveBeenAdded) return cogoToast.warn('Please add users to send requests to');
    else if (isSendingRequests) return cogoToast.warn('Please wait for the current requests to finish sending');
    else if (emailUsersToAdd.length && !selectedLookbookId) return cogoToast.error('You can only invite off platform users to a lookbook');

    /******************************************************** */
    // Get users to send requests to
    /******************************************************** */

    const platformBulkRequestLimit = isAdminControlMode(ui) ? 1e6 : brandGiftingRemaining;

    // need to filter out anyone that has already successfully been sent a request
    const filteredPlatformUsersToAdd = platformUsersToAdd.filter(u => !selectedUsersApiStateRef.current[u.id]?.success);
    const reducedPlatformUsersToAdd = filteredPlatformUsersToAdd.slice(0, platformBulkRequestLimit);
    const filteredEmailUsersToAdd = emailUsersToAdd.filter(u => !selectedUsersApiStateRef.current[u.email]?.success);
    const allUsersToAdd = [...reducedPlatformUsersToAdd, ...filteredEmailUsersToAdd];

    if (platformBulkRequestLimit === 0) return cogoToast.warn('You have no requests remaining');
    else if (allUsersToAdd.length === 0 && allRequestsSuccessful) return cogoToast.warn('All requests have already been sent');
    setIsSendingRequests(true);

    /******************************************************** */
    // Send requests
    /******************************************************** */

    for (const user of allUsersToAdd) updateUserApiStatus(user, true, null, null);
    const batchedRequests = _.chunk(allUsersToAdd, 3);
    for (const batch of batchedRequests) await Promise.all(batch.map(user => sendRequest(user)));

    /******************************************************** */
    // Handle messaging
    /******************************************************** */
    const updatedRequestState = { ...selectedUsersApiStateRef.current };
    let [successfulRequestCount, erroredRequestCount] = [0, 0];
    for (const user of allUsersToAdd) {
      const { id, email } = user;
      const { success, error } = updatedRequestState[id || email];
      if (success) ++successfulRequestCount;
      else if (error) ++erroredRequestCount;
    }

    if (!erroredRequestCount) {
      cogoToast.success(`All requests sent successfully`);
      closeAfterSubmission();
    } else if (!successfulRequestCount) cogoToast.error(`No requests were sent`);
    else cogoToast.warn(`Sent ${successfulRequestCount} requests, ${erroredRequestCount} ${erroredRequestCount === 1 ? 'failed' : 'failed'}`);

    setBrandGiftingRemaining(Math.max(brandGiftingRemaining - successfulRequestCount, 0));
    setIsSendingRequests(false);
  };

  const sendRequest = user => {
    return new Promise(resolve => {
      const requestData = _.omitBy({ brandMessage: message, Lookbook_id: selectedLookbookId, expiresOn }, _.isNil);
      user.id
        ? createSamplesRequest(user, requestData)
            .then(resp => (resp?.request ? updateUserApiStatus(user, false, true, null) : updateUserApiStatus(user, false, false, true)))
            .catch(resp => updateUserApiStatus(user, false, false, true))
            .finally(() => resolve())
        : sendLookbookOffPlatformInvitation({ ...requestData, email: user.email })
            .then(() => updateUserApiStatus(user, false, true, null))
            .catch(err => {
              updateUserApiStatus(user, false, false, true, err);
            })
            .finally(() => resolve());
    });
  };

  // overrides scoping allowing us to update the state of each user as we send requests
  const updateUserApiStatus = (user, loading, success, error, message) => {
    const currentApiState = { ...selectedUsersApiStateRef.current };

    if (loading || success || error) currentApiState[user.id || user.email] = { loading, success, error, message };
    else delete currentApiState[user.id || user.email];

    setSelectedUsersApiState(currentApiState);
    selectedUsersApiStateRef.current = currentApiState;
  };

  const removeInvalidLocationUsers = () => {
    const filteredPlatformUsersToAdd = platformUsersToAdd.filter(u => !invalidLocationUserIds.includes(u.id));
    setPlatformUsersToAdd(filteredPlatformUsersToAdd);
  };

  const findIfUserAlreadyReceivedRequestOfType = user => {
    const filterForNoLookbookSelected = r => !r.Lookbook_id && r.User_id === user.id;
    const filterForLookbookSelected = r => r.Lookbook_id && r.User_id === user.id && r.Lookbook_id === selectedLookbookId;
    const userHasRequest = requests.some(selectedLookbookId ? filterForLookbookSelected : filterForNoLookbookSelected);

    const userHasOffPlatformRequest = offPlatformLookbookInvites.some(
      invite => invite.email === curSearchVal && selectedLookbookId === invite.Lookbook_id
    );

    const userHasRequestOfType = userHasRequest || userHasOffPlatformRequest;
    return userHasRequestOfType;
  };

  const allowedCountryCodesArr = selectedGiftingOption?.allowedCountryCodes?.split(',') || [];

  const somePlatformUsersHaveAlreadyReceivedRequestType = platformUsersToAdd.some(findIfUserAlreadyReceivedRequestOfType);
  const someOffPlatformUsersHaveAlreadyReceivedRequestType = emailUsersToAdd.some(findIfUserAlreadyReceivedRequestOfType);
  const someUsersHaveAlreadyReceivedRequestType =
    somePlatformUsersHaveAlreadyReceivedRequestType || someOffPlatformUsersHaveAlreadyReceivedRequestType;

  const totalToSend = platformUsersToAdd.filter(u => !findIfUserAlreadyReceivedRequestOfType(u)).length + emailUsersToAdd.length;
  const numberOverLimit = totalToSend - brandGiftingRemaining;
  const canSend = totalToSend && totalToSend <= brandGiftingRemaining;

  return (
    <Modal
      visible={true}
      forceOuter={forceOuter}
      close={close}
      className='bulk-gifting-modal-outer'
      innerClassName='bulk-gifting-modal-inner'
      showClose={true}
    >
      <div className='bulk-gifting-modal'>
        <div className='bulk-gifting-title-container'>
          <div className='bulk-gifting-title'>Send Gifting Requests</div>
          <div className='gifting-remaining-container'>
            <div className='gifting-remaining-label'>Requests Remaining</div>
            {!_.isNil(brandGiftingRemaining) ? (
              <Tooltip
                message={
                  brandGiftingRemaining < 1e4
                    ? isSubscribedToFeature(user, 'GIFTING_LITE')
                      ? `You will get 25 new requests on ${brandGiftingReupped ||
                          'the first day of the month'}. To upgrade to recieve more gifting, please go to your subscriptions page.`
                      : `You will get 50 new requests on ${brandGiftingReupped ||
                          'the first day of the month'}. To upgrade to unlimited gifting, please go to your subscriptions page.`
                    : 'You are currently on the unlimited gifting package.'
                }
                getIconDiv={() => <span className='gifting-remaining'>{brandGiftingRemaining > 1e4 ? 'Unlimited' : brandGiftingRemaining}</span>}
              />
            ) : (
              <Loader size={30} />
            )}
          </div>
        </div>

        {/* <OutsideClickHandler onOutsideClick={handleBlurred}> */}
        <div className='bulk-gifting-section'>
          <div className='bulk-gifting-section-title'>
            Who do you want to send it to?
            {someUsersHaveAlreadyReceivedRequestType && (
              <div className='bulk-gifting-section-subtitle'>Greyed out users have already received a request for this gifting option</div>
            )}
          </div>
          {(!!platformUsersToAdd.length || !!emailUsersToAdd.length) && (
            <BulkGiftingModalSelectedUsers
              invalidLocationUserIds={invalidLocationUserIds}
              platformUsersToAdd={platformUsersToAdd}
              emailUsersToAdd={emailUsersToAdd}
              removeUser={removeUser}
              selectedUsersApiState={selectedUsersApiState}
              openArtistModal={openArtistModal}
              findIfUserAlreadyReceivedRequestOfType={findIfUserAlreadyReceivedRequestOfType}
            />
          )}

          <BulkGiftingModalSearch
            brand={brand}
            lists={lists}
            talent={talent}
            selectUsers={selectUsers}
            removeUser={removeUser}
            selectedLookbookId={selectedLookbookId}
            selectedUserIdSet={selectedUserIdSet}
            selectedEmailSet={selectedEmailSet}
            openArtistModal={openArtistModal}
            handleBlurred={handleBlurred}
            handleFocused={handleFocused}
            isFocused={isFocused}
            curSearchVal={curSearchVal}
            setCurSearchVal={setCurSearchVal}
            findIfUserAlreadyReceivedRequestOfType={findIfUserAlreadyReceivedRequestOfType}
          />
        </div>
        <div className='bulk-gifting-section'>
          <div className='bulk-gifting-section-title'>
            What are you sending?
            {!!lookbooks?.length && (
              <span className='bulk-gifting-section-title-disclaimer'>
                {lookbooks?.length} active {lookbooks?.length === 1 ? 'lookbook' : 'lookbooks'}
              </span>
            )}
          </div>
          <div className='bulk-gifting-subsection'>
            <div className='filter-container'>
              <OutsideClickHandler onOutsideClick={closeGiftingOptionsSelect}>
                <div className='filter-label' onClick={giftingOptionsSelectIsOpen ? closeGiftingOptionsSelect : openGiftingOptionsSelect}>
                  {selectedGiftingOption?.title}
                  <div>
                    <div className='filter-label-spacer'></div>
                    <FontAwesomeIcon icon={faArrowDown} />
                  </div>
                </div>
                {giftingOptionsSelectIsOpen && (
                  <div className='filter-dropdown'>
                    <div
                      className={cn('filter-option', { active: !selectedGiftingOption.id })}
                      key='General Gifting Request'
                      onClick={() => {
                        setSelectedGiftingOption({ title: 'General Gifting Request' });
                        closeGiftingOptionsSelect();
                      }}
                    >
                      General Gifting Request
                    </div>
                    {lookbooks.map(lookbook => {
                      const { id, title } = lookbook;
                      return (
                        <div
                          className={cn('filter-option', { active: selectedGiftingOption.id === lookbook.id })}
                          key={`filter-option-${id}-${title}`}
                          onClick={() => {
                            setSelectedGiftingOption(lookbook);
                            closeGiftingOptionsSelect();
                          }}
                        >
                          {lookbook.title}
                        </div>
                      );
                    })}
                  </div>
                )}
              </OutsideClickHandler>
            </div>
            {!selectedGiftingOption.id && (
              <span className='bulk-gifting-section-title-disclaimer'>
                Gifting requests without lookbooks require you to manually generate the order on your POS system and place the order tracking number
                in here to alert the creator the package has been sent
              </span>
            )}
          </div>
        </div>
        {!!selectedGiftingOption && (
          <div className={cn('bulk-gifting-location-specific', { active: selectedGiftingOption.allowedCountryCodes })}>
            <FontAwesomeIcon icon={faLocationArrow} />
            <span>
              {selectedGiftingOption.allowedCountryCodes ? `This is a ${allowedCountryCodesArr.join(', ')} Only Lookbook` : 'No Country Restrictions'}
            </span>
            <Tooltip
              message={`To configure which addresses are valid for this Lookbook please navigate to Lookbook settings`}
              getIconDiv={() => <FontAwesomeIcon icon={faInfoCircle} />}
            />
          </div>
        )}
        <div className='bulk-gifting-section'>
          <div className='bulk-gifting-section-title'>Add a message</div>
          <div className='bulk-gifting-message-container'>
            <BulkGiftingModalMessage user={user} message={message} setMessage={setMessage} updateBrandSettings={props.updateBrandSettings} />
          </div>
        </div>
        <div className='bulk-gifting-section'>
          <div className='bulk-gifting-section-title'>
            Set a Response Deadline
            <div className='bulk-gifting-section-subtitle'>After this date creators will no longer be able to accept.</div>
          </div>
          <div className='bulk-gifting-set-expiration-container'>
            <DateSelector placeholder='Select Date' isSingleDate date={expiresOn} onDateChange={date => setExpiresOn(date)} />
            {expiresOn ? (
              <div className='clear-expiration' onClick={() => setExpiresOn(null)}>
                Clear Expiration
              </div>
            ) : (
              mostRecentlySentRequest?.expiresOn && (
                <div className='set-expiration-from-last-selected' onClick={() => setExpiresOn(moment(mostRecentlySentRequest.expiresOn))}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Set to {moment(mostRecentlySentRequest.expiresOn).format('MMM Do')}
                </div>
              )
            )}
          </div>
        </div>
      </div>
      <div className='bulk-gifting-footer'>
        <div className='disclaimer-container'></div>
        <div className={cn('bulk-gifting-footer-buttons', { isDisabled: !usersHaveBeenAdded || isSendingRequests || allRequestsSuccessful })}>
          <button className='bulk-gifting-footer-button' onClick={close}>
            Cancel
          </button>
          {invalidLocationUserIds.length > 0 && (
            <button className='bulk-gifting-footer-button warn-button' onClick={removeInvalidLocationUsers}>
              {`Remove Creators Outside ${allowedCountryCodesArr.slice(0, 2).join(', ')}${allowedCountryCodesArr.length > 2 ? '...' : ''} (${
                invalidLocationUserIds.length
              })`}
            </button>
          )}
          <button className={cn('bulk-gifting-footer-button send-requests-button', { inactive: !canSend })} onClick={sendRequests}>
            {someRequestsErrored
              ? 'Try Again'
              : brandGiftingRemaining === 0
              ? 'Out of Requests'
              : numberOverLimit > 0
              ? `${numberOverLimit} Over Sending Limit`
              : `Send ${totalToSend > 1 ? `${totalToSend} Requests` : 'Request'}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

BulkGiftingModal.propTypes = {
  user: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  talent: PropTypes.object.isRequired,
  openArtistModal: PropTypes.func.isRequired,
  closeGiftingModal: PropTypes.func.isRequired,
  createSamplesRequest: PropTypes.func.isRequired,
  updateBrandSettings: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { user, ui, talent, analytics } = state;
  return { user, ui, talent, analytics };
};

export default connect(mapStateToProps, {
  createSamplesRequest,
  closeGiftingModal,
  openArtistModal,
  updateBrandSettings
})(BulkGiftingModal);
